import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import RestaurantCard from "@/components/RestaurantCard";
import { Search } from "lucide-react";

const CUISINES = ["pizza", "burgers", "fish-chips", "indian", "steak", "mexican", "healthy", "desserts", "drinks"];

const Restaurants = () => {
  const { t, cuisineLabel } = useLang();
  const [params, setParams] = useSearchParams();
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const activeCuisine = params.get("cuisine") || "";

  useEffect(() => { api.get("/restaurants").then(r => setAll(r.data)); }, []);

  const filtered = useMemo(() => {
    let list = all;
    if (activeCuisine) list = list.filter(r => r.cuisine === activeCuisine);
    if (q) list = list.filter(r => r.name.toLowerCase().includes(q.toLowerCase()));
    return list;
  }, [all, activeCuisine, q]);

  const setCuisine = (c) => {
    const p = new URLSearchParams(params);
    if (!c) p.delete("cuisine"); else p.set("cuisine", c);
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
      <h1 className="font-heading text-3xl sm:text-5xl font-extrabold text-navy tracking-tight">{t("restaurants")}</h1>
      <p className="mt-2 text-navy/60">Caleta de Fuste, Fuerteventura</p>

      <div className="mt-6 relative max-w-lg">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("filterSearch")}
          data-testid="restaurant-search"
          className="w-full pl-10 pr-4 py-3 rounded-full bg-white border border-navy/10 focus:border-coral focus:outline-none text-sm"
        />
      </div>

      <div className="mt-6">
        <p className="text-xs font-semibold uppercase tracking-widest text-navy/50 mb-3">{t("quickFilters")}</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCuisine("")}
            data-testid="filter-all"
            className={`px-4 py-2 rounded-full text-sm font-semibold transition ${!activeCuisine ? "bg-navy text-white" : "bg-white border border-navy/15 text-navy hover:border-coral"}`}
          >{t("all")}</button>
          {CUISINES.map(c => (
            <button
              key={c}
              onClick={() => setCuisine(c)}
              data-testid={`filter-${c}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${activeCuisine === c ? "bg-navy text-white" : "bg-white border border-navy/15 text-navy hover:border-coral"}`}
            >{cuisineLabel(c)}</button>
          ))}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="restaurants-grid">
        {filtered.map((r, i) => <RestaurantCard key={r.id} r={r} index={i} />)}
      </div>
      {filtered.length === 0 && (
        <p className="text-center text-navy/50 py-16">No restaurants found.</p>
      )}
    </div>
  );
};

export default Restaurants;
